/**
 * Focus+Context Filter — Desaturation 策略
 *
 * 战略: 废弃正片叠底 (Multiply Darkening) 方法。
 *
 * 底层 (Base Layer):  纯净的密度颜色图 (RGB) — 绝对真值, 不做任何修改
 * 遮罩效果:           杂乱区域去饱和 → 灰度 ("黑白照片" 效果)
 * 斜线纹理:           自适应颜色 — 深色背景用白线, 浅色背景用黑线
 *
 * 视觉效果:
 *   杂乱区域 → 去饱和变灰 + 高对比度自适应斜线 → 在任何 Colormap 上清晰可辨
 *   整齐区域 → 保持原始鲜艳色彩 → 准确传达密度信息
 */

import { Filter, GpuProgram, UniformGroup, Texture } from 'pixi.js';
import source from '../shaders/focusContext.wgsl';

export interface FocusContextFilterOptions {
    /** Messiness texture (单通道, 0=整齐, 1=杂乱) */
    messinessTexture: Texture;
    /** 杂乱度容忍阈值 (0.0–1.0, 高于此值开始遮罩, default 0.5) */
    threshold?: number;
    /** 去饱和强度 (0.0=保持彩色, 1.0=完全灰度, default 1.0) */
    saturationDrop?: number;
    /** 边缘羽化宽度 (default 0.1) */
    transition?: number;
    /** 斜线纹理密度 (default 80.0, 越大线越密) */
    hatchDensity?: number;
    /** 斜线粗细系数 (0.0–1.0, default 0.25) */
    hatchThickness?: number;
    /** 斜线不透明度 (0.0–1.0, default 0 — 当前禁用) */
    hatchOpacity?: number;
}

export class FocusContextFilter extends Filter {
    private messinessTexture: Texture;

    constructor(options: FocusContextFilterOptions) {
        const {
            messinessTexture,
            threshold = 0.5,
            saturationDrop = 1.0,
            transition = 0.1,
            hatchDensity = 80.0,
            hatchThickness = 0.25,
            hatchOpacity = 0,
        } = options;

        const gpuProgram = GpuProgram.from({
            vertex: {
                source,
                entryPoint: 'mainVertex',
            },
            fragment: {
                source,
                entryPoint: 'mainFragment',
            },
        });

        const filterUniforms = new UniformGroup({
            uThreshold: { value: threshold, type: 'f32' },
            uSaturationDrop: { value: saturationDrop, type: 'f32' },
            uTransition: { value: transition, type: 'f32' },
            uHatchDensity: { value: hatchDensity, type: 'f32' },
            uHatchThickness: { value: hatchThickness, type: 'f32' },
            uHatchOpacity: { value: hatchOpacity, type: 'f32' },
        });

        super({
            gpuProgram,
            resources: {
                filterUniforms,
                uMessinessMap: messinessTexture.source,
                uMessinessSampler: messinessTexture.source.style,
            },
        });

        this.messinessTexture = messinessTexture;
        this.padding = 0;
    }

    // ---- Public API ----

    /** 更新杂乱度容忍阈值 (0–1, 高于此值开始遮罩) */
    public setThreshold(value: number): void {
        (this.resources.filterUniforms as UniformGroup).uniforms.uThreshold = value;
    }

    /** 更新去饱和强度 (0=保持彩色, 1=完全灰度) */
    public setSaturationDrop(value: number): void {
        (this.resources.filterUniforms as UniformGroup).uniforms.uSaturationDrop = value;
    }

    /** 更新边缘羽化宽度 (0–1) */
    public setTransition(value: number): void {
        (this.resources.filterUniforms as UniformGroup).uniforms.uTransition = value;
    }

    /** 更新斜线纹理密度 */
    public setHatchDensity(value: number): void {
        (this.resources.filterUniforms as UniformGroup).uniforms.uHatchDensity = value;
    }

    /** 更新斜线粗细 (0–1) */
    public setHatchThickness(value: number): void {
        (this.resources.filterUniforms as UniformGroup).uniforms.uHatchThickness = value;
    }

    /** 更新斜线不透明度 (0–1) */
    public setHatchOpacity(value: number): void {
        (this.resources.filterUniforms as UniformGroup).uniforms.uHatchOpacity = value;
    }

    /** 批量更新所有参数 */
    public updateParams(params: {
        threshold?: number;
        saturationDrop?: number;
        transition?: number;
        hatchDensity?: number;
        hatchThickness?: number;
        hatchOpacity?: number;
    }): void {
        const u = (this.resources.filterUniforms as UniformGroup).uniforms;
        if (params.threshold !== undefined) u.uThreshold = params.threshold;
        if (params.saturationDrop !== undefined) u.uSaturationDrop = params.saturationDrop;
        if (params.transition !== undefined) u.uTransition = params.transition;
        if (params.hatchDensity !== undefined) u.uHatchDensity = params.hatchDensity;
        if (params.hatchThickness !== undefined) u.uHatchThickness = params.hatchThickness;
        if (params.hatchOpacity !== undefined) u.uHatchOpacity = params.hatchOpacity;
    }

    /** 替换杂乱度纹理 */
    public updateMessinessTexture(texture: Texture): void {
        this.messinessTexture = texture;
        this.resources.uMessinessMap = texture.source;
        this.resources.uMessinessSampler = texture.source.style;
    }
}
